import {makeAutoObservable} from 'mobx';
import {persistence, StorageAdapter} from 'mobx-persist-store';
import {writeStore, readStore} from 'helpers/mobx-persist';
import getBrowserTheme from 'helpers/get-browser-theme';
import getBrowserLanguage from 'helpers/get-browser-language';

export enum Theme {
  Light = 'light',
  Dark = 'dark'
}

export enum Language {
  Russian = 'ru-RU',
  English = 'en-EN'
}

export interface Config {
  theme: Theme;
  language: Language;
}

/**
 * The app store
 * Note: Used to store service information about the state of the application
 */
export class AppStore {
  private _theme: Theme;
  private _language: Language;
  private _isSidebarToggle: boolean = true;

  /**
   * Configures states
   * @param {Config} config
   */
  constructor(config: Config) {
    this._theme = config.theme;
    this._language = config.language;

    makeAutoObservable(this);
  }

  /**
   * Get the theme
   * @return {Theme} The current theme
   */
  get theme(): Theme {
    return this._theme;
  }

  /**
   * Set the theme
   * @param {Theme} theme
   */
  set theme(theme: Theme) {
    this._theme = theme;
  }

  /**
   * Get the language
   * @return {Language} The current language
   */
  get language(): Language {
    return this._language;
  }

  /**
   * Set the language
   * @param {Language} language
   */
  set language(language: Language) {
    this._language = language;
  }

  /**
   * Get the sidebar toggle
   * @return {boolean} The is sidebar toggle
   */
  get isSidebarToggle(): boolean {
    return this._isSidebarToggle;
  }

  /**
   * Set the sidebar toggle
   * @param {boolean} isToggle
   */
  set isSidebarToggle(isToggle: boolean) {
    this._isSidebarToggle = isToggle;
  }
}

export default persistence({
  name: 'AppStore',
  properties: ['_theme', '_language', '_isSidebarToggle'],
  adapter: new StorageAdapter({
    read: readStore,
    write: writeStore,
  }),
})(new AppStore({
  theme: getBrowserTheme(),
  language: getBrowserLanguage(Language.English),
}));
